import styled from '@emotion/styled';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import TinyHeader from '@shared/components/core/tiny/TinyHeader';
import { BodyXSmall, BodyXXSmall } from '@shared/components/core/text/Typography';

export interface IExtensionModeOption<TMode extends string> {
    value: TMode;
    label: string;
    description: string;
}

export interface IExtensionModeToggleProps<TMode extends string> {
    /** Heads the group and names it for screen readers, e.g. "How this skill is used". */
    label: string;
    options: IExtensionModeOption<TMode>[];
    value: TMode;
    onChange: (mode: TMode) => void;
    /**
     * Drops the heading and the per-option sentences down to a segmented row of labels, for an action rail
     * where the full three-box group won't fit. The descriptions move to each option's tooltip and the radio
     * glyphs become visually-hidden (still focusable), so the selection reads from the accent box alone.
     */
    compact?: boolean;
}

/**
 * The Off / Auto / Manual choice shared by the skill and tool detail dialogs, so the same decision
 * looks and behaves identically wherever it's offered.
 *
 * Three boxed radios in a row rather than a segmented control: every option carries a sentence
 * explaining what it does, and the difference between Auto and Manual is the whole decision being
 * made here. All three read at once, without hovering — which a keyboard or touch user can't do
 * anyway — and side by side they compare directly.
 */
export default function ExtensionModeToggle<TMode extends string>({
    label,
    options,
    value,
    onChange,
    compact,
}: Readonly<IExtensionModeToggleProps<TMode>>) {
    return (
        <>
            {!compact && <TinyHeader size="small" title={label} />}
            <ModeRadioGroup
                aria-label={label}
                $compact={compact}
                value={value}
                onChange={(_event, next) => onChange(next as TMode)}
                name={label}>
                {options.map((option) => {
                    const choice = (
                        <ModeChoice
                            key={option.value}
                            value={option.value}
                            $selected={value === option.value}
                            $compact={compact}
                            control={<Radio size="small" />}
                            // The label is two lines of our own typography, not MUI's — a label may only hold
                            // phrasing content, so both render as spans.
                            disableTypography={true}
                            label={
                                <ModeText>
                                    <ModeLabel as="span" $selected={value === option.value} $compact={compact}>
                                        {option.label}
                                    </ModeLabel>
                                    {!compact && <ModeDescription as="span">{option.description}</ModeDescription>}
                                </ModeText>
                            }
                        />
                    );
                    // Compact drops the sentence from the box, so the tooltip is the only place left that
                    // explains what the option does.
                    return compact ? (
                        <Tooltip key={option.value} title={option.description} placement="top">
                            {choice}
                        </Tooltip>
                    ) : (
                        choice
                    );
                })}
            </ModeRadioGroup>
        </>
    );
}

/** Three equal columns; they wrap rather than squeeze when the dialog is narrow. */
const ModeRadioGroup = styled(RadioGroup, {
    shouldForwardProp: (prop) => prop !== '$compact',
})<{ $compact?: boolean }>`
    display: flex;
    flex-direction: row;
    flex-wrap: ${(props) => (props.$compact ? 'nowrap' : 'wrap')};
    /* Compact butts the boxes together into one segmented control; the full group keeps them as three
       separate cards, each holding a label and its own sentence. */
    gap: ${(props) => (props.$compact ? '0' : '0.5rem')};
`;

/**
 * Each choice is its own bordered box so the label and its sentence read as one unit, and the picked
 * one is outlined in the accent — the selection is legible from the box, not just the radio dot.
 */
const ModeChoice = styled(FormControlLabel, {
    shouldForwardProp: (prop) => prop !== '$selected' && prop !== '$compact',
})<{ $selected?: boolean; $compact?: boolean }>`
    flex: ${(props) => (props.$compact ? '0 0 auto' : '1 1 10rem')};
    align-items: ${(props) => (props.$compact ? 'center' : 'flex-start')};
    margin: 0;
    padding: ${(props) => (props.$compact ? '0.15rem 0.4rem' : '0.5rem 0.6rem')};
    border: 1px solid ${(props) => (props.$selected ? props.theme.color.accent : props.theme.color.border)};
    border-radius: 0.5rem;
    background: ${(props) => (props.$selected ? props.theme.color.cardHover : 'transparent')};

    & .MuiRadio-root {
        padding: 0 0.4rem 0 0;
    }
    ${(props) =>
        props.$compact &&
        `
        /* Only the outer edges of the group are rounded, and each box overlaps the one before it so their
           borders collapse into a single shared rule — three options reading as one control rather than as
           three unrelated pills. The picked box lifts above its neighbours so its accent border stays whole
           on both sides. */
        border-radius: 0;
        position: relative;
        z-index: ${props.$selected ? 1 : 0};

        &:first-of-type {
            border-radius: 0.4rem 0 0 0.4rem;
        }
        &:last-of-type {
            border-radius: 0 0.4rem 0.4rem 0;
        }
        &:not(:first-of-type) {
            margin-left: -1px;
        }

        /* Visually hidden rather than removed: the input stays in the tab order and keeps the group operable
           by keyboard, while the box itself carries the selection. */
        & .MuiRadio-root {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            overflow: hidden;
            clip-path: inset(50%);
        }
    `}
    &:hover {
        border-color: ${(props) => props.theme.color.accent};
        z-index: 1;
    }
    &:has(.Mui-focusVisible) {
        outline: 2px solid ${(props) => props.theme.color.accent};
        outline-offset: -2px;
        z-index: 1;
    }
`;

const ModeText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
`;

/**
 * `BodyXSmall` is 0.875rem — fine in the dialog, where the group is the section's content, but larger than
 * the title of the row it sits beside in compact. There it drops below the metadata line's 0.75rem and only
 * the picked option takes the full text colour, so the control reads as a setting on the row rather than
 * competing with the name for the first glance.
 */
const ModeLabel = styled(BodyXSmall)<{ $selected?: boolean; $compact?: boolean }>`
    font-weight: ${(props) => (props.$selected ? 600 : 400)};
    color: ${(props) => (props.$compact && !props.$selected ? props.theme.color.textBody : props.theme.color.text)};
    ${(props) =>
        props.$compact &&
        `
        font-size: 0.6875rem;
        line-height: 1.2;
        letter-spacing: 0.01em;
    `}
`;

const ModeDescription = styled(BodyXXSmall)``;
